<script setup lang="ts">
import { nothing } from "../lib/maybe";
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";

defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const session = useEditorSessionStore();
const { modLabel, altLabel } = storeToRefs(session);

type ShortcutRow = { keys: string[]; action: string };
type ShortcutGroup = { title: string; items: ShortcutRow[] };

const groups = computed<ShortcutGroup[]>(() => {
  const mod = modLabel.value;
  // `altLabel` is `⌥` or `Alt+` for concatenated hints; chip labels want bare `Alt`.
  const altBare = altLabel.value.replace(/\+$/, "");
  const alt = altBare.length > 0 ? altBare : "Alt";
  return [
    {
      title: "Navigate",
      items: [
        { keys: [mod, "1"], action: "Writing" },
        { keys: [mod, "2"], action: "Timeline" },
        { keys: [mod, "3"], action: "Scratchpad" },
        { keys: [mod, "4"], action: "Worldbuilding" },
        { keys: [mod, "5"], action: "Trash" },
        { keys: [mod, "⇧", "P"], action: "Search project" },
      ],
    },
    {
      title: "View",
      items: [
        { keys: [mod, "⇧", "F"], action: "Focus mode" },
        { keys: [mod, "⇧", "R"], action: "Reading mode" },
        { keys: ["Esc"], action: "Exit focus / reading mode" },
      ],
    },
    {
      title: "File",
      items: [
        { keys: [mod, "⇧", "N"], action: "New project" },
        { keys: [mod, "N"], action: "New (in current section)" },
        { keys: [mod, "O"], action: "Open project" },
        { keys: [mod, "S"], action: "Save" },
      ],
    },
    {
      title: "Editing",
      items: [
        { keys: [mod, "Z"], action: "Undo" },
        { keys: [mod, "⇧", "Z"], action: "Redo" },
        { keys: [mod, "F"], action: "Find in document" },
        { keys: [mod, "G"], action: "Find next" },
        { keys: [mod, "D"], action: "Select next match" },
        { keys: [mod, "⇧", "Y"], action: "Thesaurus" },
        { keys: [mod, "A"], action: "Select all" },
        { keys: [alt, "↑/↓"], action: "Move line" },
        { keys: [alt, "⇧", "↑/↓"], action: "Duplicate line" },
        { keys: [mod, alt, "↑/↓"], action: "Add cursor above/below" },
      ],
    },
    {
      title: "Interface",
      items: [
        { keys: [mod, "+"], action: "Zoom in" },
        { keys: [mod, "−"], action: "Zoom out" },
        { keys: [mod, "0"], action: "Actual size" },
        { keys: [mod, "/"], action: "Show this list" },
      ],
    },
  ];
});

/** Left: chrome navigation. Right: editing + interface. */
const columns = computed(() => {
  const all = groups.value;
  return [all.slice(0, 3), all.slice(3)] as const;
});
</script>

<template>
  <ZwDialog
    :open="open"
    title="Keyboard shortcuts"
    size="lg"
    show-close
    :z-index="130"
    :description="nothing"
    @close="emit('close')"
  >
    <div class="zw-shortcuts">
      <div
        v-for="(column, colIndex) in columns"
        :key="colIndex"
        class="zw-shortcuts-col"
      >
        <section
          v-for="group in column"
          :key="group.title"
          class="zw-shortcuts-group"
        >
          <h3 class="zw-shortcuts-heading">{{ group.title }}</h3>
          <ul class="zw-shortcuts-list">
            <li
              v-for="item in group.items"
              :key="item.action"
              class="zw-shortcuts-row"
            >
              <span class="zw-shortcuts-action">{{ item.action }}</span>
              <span class="zw-shortcuts-keys">
                <kbd
                  v-for="(key, index) in item.keys"
                  :key="`${item.action}-${index}`"
                  class="zw-shortcuts-kbd"
                >
                  {{ key }}
                </kbd>
              </span>
            </li>
          </ul>
        </section>
      </div>
    </div>
    <template #footer>
      <ZwButton variant="primary" @click="emit('close')">Close</ZwButton>
    </template>
  </ZwDialog>
</template>

<style scoped>
.zw-shortcuts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 2.25rem;
}

.zw-shortcuts-col {
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
  min-width: 0;
}

.zw-shortcuts-heading {
  margin: 0 0 0.35rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.zw-shortcuts-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.zw-shortcuts-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.32rem 0;
  border-bottom: 1px solid color-mix(in oklch, var(--line) 70%, transparent);
  font-size: 0.88rem;
}

.zw-shortcuts-row:last-child {
  border-bottom: 0;
}

.zw-shortcuts-action {
  color: var(--text);
  min-width: 0;
}

.zw-shortcuts-keys {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  justify-content: flex-end;
  flex-shrink: 0;
}

.zw-shortcuts-kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4rem;
  padding: 0.15rem 0.4rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 0.75rem;
  line-height: 1.2;
}

@media (max-width: 36rem) {
  .zw-shortcuts {
    grid-template-columns: 1fr;
  }
}
</style>
